import React, { useEffect, useState } from 'react';
import { Card } from './components/UI';
import { useUmami, UmamiEvents } from './hooks/useUmami';
import { useTranslation, SUPPORTED_LANGUAGES } from './i18n';
import { LanguageSwitcher } from './components/LanguageSwitcher';

const WEREPRESENT_LINKS = {
  privacy: 'https://werepresent.nl/privacy',
  voorwaarden: 'https://werepresent.nl/voorwaarden',
  aiPolicy: 'https://werepresent.nl/ai-policy',
};

export function AuthScreen({ onLogin, apiBase, defaultType }) {
  const { t, language, setLanguage } = useTranslation();
  const [isRegistering, setIsRegistering] = useState(false);
  const [type] = useState(defaultType || 'candidate'); 
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationBusy, setVerificationBusy] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [unverifiedLoginEmail, setUnverifiedLoginEmail] = useState('');
  const { trackEvent } = useUmami();
  
  // Password reset state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState('email'); // 'email' | 'code' | 'newpassword' | 'success'
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotCode, setForgotCode] = useState('');
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
  
  // Form State: Bevat nu ALLE mogelijke velden
  const [formData, setFormData] = useState({
    email: '',  // Email is nu de identifier (was: username)
    password: '',
    // Shared
    name: '', 
    phone: '',
    location: '',  // Locatie (gebruikt door beide: vestigingsplaats bedrijf / woonplaats kandidaat)
    // Company Specific
    company_name: '',  // Bedrijfsnaam
    sector: '',
    one_liner_description: '',
    function: '', // Let op: function is een reserved keyword, maar mag als object key
    // Candidate Specific
    gender: '',  // NIEUW: geslacht
    education_history: '',
    work_experience: '',  // NIEUW: werkervaring
    age: '',
    // AVG Consent
    consent_privacy: false,
    consent_terms: false,
    consent_ai: false,
    language_preference: language,
    // Honeypot (anti-bot)
    website: ''  // Hidden field - bots fill this, humans don't
  });

  // Helper voor API calls
  async function postAuth(endpoint, body) {
    const url = `${apiBase}${endpoint}`;
    const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.detail || t('common.unknown_error'));
    }
    return data;
  }

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const interval = setInterval(() => {
      setResendCooldown(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCooldown]);

  // Password reset handlers
  const resetForgotPasswordState = () => {
    setShowForgotPassword(false);
    setForgotStep('email');
    setForgotEmail('');
    setForgotCode('');
    setForgotNewPassword('');
    setForgotConfirmPassword('');
    setError(null);
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);

    try {
      if (forgotStep === 'email') {
        // Step 1: Request reset code
        await postAuth(`/api/${type}/auth/forgot-password`, {
          email: forgotEmail,
          language_preference: language,
        });
        setForgotStep('code');
      } else if (forgotStep === 'code') {
        // Step 2: Validate password and confirm
        if (forgotNewPassword.length < 8) {
          throw new Error(t('auth.password_too_short'));
        }
        if (forgotNewPassword !== forgotConfirmPassword) {
          throw new Error(t('auth.password_mismatch'));
        }
        // Step 3: Reset password with code
        await postAuth(`/api/${type}/auth/reset-password`, {
          email: forgotEmail,
          code: forgotCode,
          new_password: forgotNewPassword,
          language_preference: language,
        });
        setForgotStep('success');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);

    const mode = isRegistering ? 'register' : 'login';
    const endpoint = `/api/${type}/auth/${mode}`;

    try {
        // Basis payload - email is nu de identifier
        const payload = {
            email: formData.email,
            password: formData.password,
            // Actieve UI-taal meesturen: bij login helpt dit voor pre-auth
            // foutmeldingen (bv. "Login mislukt"); bij register wordt dit
            // opgeslagen als User.language_preference.
          language_preference: isRegistering ? (formData.language_preference || language) : language,
        };

        // Alleen extra data toevoegen bij registratie
        if (isRegistering) {
            // Honeypot (anti-bot)
            payload.website = formData.website;
            
            // Consent velden (verplicht bij registratie)
            payload.consent_privacy = formData.consent_privacy;
            payload.consent_terms = formData.consent_terms;
            payload.consent_ai = formData.consent_ai;
            
            if (type === 'company') {
                // Specifieke velden voor BEDRIJF
                Object.assign(payload, {
                    name: formData.name,
                    phone: formData.phone,
                    company_name: formData.company_name,
                    sector: formData.sector,
                    location: formData.location,
                    one_liner_description: formData.one_liner_description,
                    function: formData.function
                });
            } else {
                // Specifieke velden voor KANDIDAAT
                Object.assign(payload, {
                    name: formData.name,
                    phone: formData.phone,
                    gender: formData.gender,
                    education_history: formData.education_history,
                    work_experience: formData.work_experience,
                    location: formData.location,
                    age: formData.age
                });
            }
        }

        const res = await postAuth(endpoint, payload);

        if (isRegistering && payload.language_preference) {
          setLanguage(payload.language_preference);
        }

        if (res.ok && res.verification_required) {
          setPendingVerification({
            email: res.email,
            user_id: res.user_id
          });
          setVerificationCode('');
          setResendCooldown(res.resend_after_seconds || 60);
          setError(null);
          setBusy(false);
          return;
        }
        
        if (res.ok && res.user) {
            // Track successful authentication
            if (isRegistering) {
                trackEvent(UmamiEvents.SIGNUP_COMPLETED, { userType: type });
            }
            onLogin(res.user);
        }

    } catch (err) {
        const message = (err.message || '').toLowerCase();
        const unverified = message.includes('nog niet geverifieerd')
          || message.includes('not verified')
          || message.includes('email_not_verified');
        if (!isRegistering && isCandidate && unverified) {
          setUnverifiedLoginEmail(formData.email.trim().toLowerCase());
        }
        setError(err.message);
    } finally {
        setBusy(false);
    }
  };

  const handleResendFromLoginBanner = async () => {
    if (!unverifiedLoginEmail || resendCooldown > 0) {
      return;
    }

    setResendBusy(true);
    setError(null);
    try {
      const res = await postAuth('/api/candidate/auth/resend-registration-code', {
        email: unverifiedLoginEmail,
        language_preference: language,
      });
      setResendCooldown(res.resend_after_seconds || 60);
    } catch (err) {
      const waitMatch = (err.message || '').match(/(\d+)\s*(seconden|seconds)/i);
      if (waitMatch) {
        setResendCooldown(parseInt(waitMatch[1], 10));
      }
      setError(err.message);
    } finally {
      setResendBusy(false);
    }
  };

  const handleVerifyRegistrationCode = async (e) => {
    e.preventDefault();
    setError(null);

    if (!pendingVerification?.email) {
      setError(t('auth.verify_missing_session'));
      return;
    }

    if (verificationCode.trim().length !== 6) {
      setError(t('auth.verify_invalid_code'));
      return;
    }

    setVerificationBusy(true);
    try {
      const res = await postAuth('/api/candidate/auth/verify-registration-code', {
        email: pendingVerification.email,
        code: verificationCode.trim(),
        language_preference: language,
      });

      if (res.ok && res.user) {
        trackEvent(UmamiEvents.SIGNUP_COMPLETED, { userType: 'candidate' });
        onLogin(res.user);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setVerificationBusy(false);
    }
  };

  const handleResendRegistrationCode = async () => {
    if (!pendingVerification?.email || resendCooldown > 0) {
      return;
    }

    setResendBusy(true);
    setError(null);

    try {
      const res = await postAuth('/api/candidate/auth/resend-registration-code', {
        email: pendingVerification.email,
        language_preference: language,
      });
      setResendCooldown(res.resend_after_seconds || 60);
    } catch (err) {
      const waitMatch = (err.message || '').match(/(\d+)\s*(seconden|seconds)/i);
      if (waitMatch) {
        setResendCooldown(parseInt(waitMatch[1], 10));
      }
      setError(err.message);
    } finally {
      setResendBusy(false);
    }
  };

  const resetVerificationFlow = () => {
    setPendingVerification(null);
    setVerificationCode('');
    setResendCooldown(0);
    setError(null);
    setUnverifiedLoginEmail('');
  };

  const unverifiedLoginMessage = (error || '').toLowerCase().includes('geverifieerd') || (error || '').toLowerCase().includes('verified');
  const unverifiedLoginTargetEmail = unverifiedLoginEmail || (unverifiedLoginMessage ? formData.email.trim().toLowerCase() : '');
  const isCandidate = type === 'candidate';
  const useUnifiedStyle = true;
  const companyFieldClass = 'w-full min-h-[48px] border-4 border-[#151827] bg-white p-3 text-sm text-[#151827] transition-colors placeholder:text-[#151827]/35 focus:bg-[#f4efe7] focus:outline-none';

  // Helper om snel inputs te renderen - met optionele required indicator
  const renderInput = (label, key, inputType = "text", placeholder = "", required = true) => (
    <div>
        <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <input 
            required={required}
            type={inputType}
            placeholder={placeholder}
            className={useUnifiedStyle
              ? 'w-full rounded p-3 min-h-[44px] text-sm transition-colors border-2 border-[#151827] bg-[#fffdf8] focus:bg-white focus:ring-0 focus:border-[#3f7f65]'
              : companyFieldClass}
            value={formData[key]}
            onChange={e => setFormData({...formData, [key]: e.target.value})}
        />
    </div>
  );

      const renderLanguagePreferenceSelect = () => (
      <div>
        <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
          {t('common.language')}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <select
          required
          className="w-full min-h-[44px] rounded border-2 border-[#151827] bg-[#fffdf8] p-3 text-sm transition-colors focus:border-[#3f7f65] focus:bg-white focus:outline-none"
          value={formData.language_preference}
          onChange={e => setFormData({ ...formData, language_preference: e.target.value })}
        >
          {SUPPORTED_LANGUAGES.map((lang) => (
            <option key={lang} value={lang}>
              {t(lang === 'nl' ? 'common.dutch' : 'common.english')}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500">{t('auth.language_preference_hint')}</p>
      </div>
      );

  if (pendingVerification && isCandidate) {
    return (
      <div className="relative min-h-screen overflow-hidden bg-[#f6f1e8] p-4 text-[#151827]">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.10]"
          style={{
            backgroundImage: 'radial-gradient(#151827 1px, transparent 1px)',
            backgroundSize: '18px 18px',
          }}
        />
        <div className="pointer-events-none absolute -left-24 -top-24 h-80 w-80 rounded-full bg-[#3f7f65] opacity-25 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 top-8 h-[28rem] w-[28rem] rounded-full bg-[#713d8d] opacity-25 blur-3xl" />

        {/* Taalkeuze vóór login: alleen client-side, geen backend-call. */}
        <div className="relative z-20 mx-auto flex w-full max-w-md justify-end pt-2">
          <LanguageSwitcher />
        </div>

        <div className="relative z-10 flex min-h-screen items-center justify-center">
        <Card className="w-full max-w-md rounded-[1.5rem] border-4 border-[#151827] bg-white p-8" style={{ boxShadow: '12px 12px 0 #151827' }}>
          <div className="text-center mb-6">
            <div className="mb-4 inline-block bg-[#3f7f65] px-4 py-2 text-xs font-black uppercase tracking-wider text-white" style={{ clipPath: 'polygon(0 14%, 96% 0, 100% 84%, 8% 100%)' }}>
              {t('auth.talent_portal')}
            </div>
            <h1 className="text-2xl font-black text-[#3f7f65]">{t('auth.verify_email_title')}</h1>
            <p className="mt-2 text-sm text-gray-600">
              {t('auth.verify_email_intro', { email: pendingVerification.email })}
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded border border-red-200">
              {error}
            </div>
          )}

          <form onSubmit={handleVerifyRegistrationCode} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                {t('auth.verification_code')}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                required
                type="text"
                maxLength={6}
                pattern="[0-9]{6}"
                value={verificationCode}
                onChange={e => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="w-full min-h-[44px] rounded border-2 border-[#151827] bg-[#fffdf8] p-3 text-center font-mono text-xl tracking-widest focus:border-[#3f7f65] focus:bg-white focus:outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">{t('auth.code_expires_15m')}</p>
            </div>

            <button
              type="submit"
              disabled={verificationBusy || verificationCode.trim().length !== 6}
              className="w-full rounded-xl bg-[#3f7f65] py-3 font-black text-white transition hover:translate-x-0.5 hover:translate-y-0.5 disabled:opacity-50"
              style={{ boxShadow: '7px 7px 0 #151827' }}
            >
              {verificationBusy ? t('auth.verifying') : t('auth.verify_continue')}
            </button>
          </form>

          <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
            <button
              type="button"
              onClick={handleResendRegistrationCode}
              disabled={resendBusy || resendCooldown > 0}
              className="w-full rounded-xl border-2 border-[#151827] py-2 font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
            >
              {resendBusy
                ? t('auth.sending')
                : resendCooldown > 0
                  ? t('auth.new_code_in_seconds', { seconds: resendCooldown })
                  : t('auth.resend_code')}
            </button>

            <button
              type="button"
              onClick={resetVerificationFlow}
              className="w-full py-2 text-sm text-gray-500 hover:text-gray-700"
            >
              {t('auth.back_to_register')}
            </button>
          </div>
        </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#f6f1e8] p-4 text-[#151827]">
      <>
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.10]"
          style={{
            backgroundImage: 'radial-gradient(#151827 1px, transparent 1px)',
            backgroundSize: '18px 18px',
          }}
        />
        <div className="pointer-events-none absolute -left-24 -top-24 h-80 w-80 rounded-full bg-[#3f7f65] opacity-25 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 top-8 h-[28rem] w-[28rem] rounded-full bg-[#713d8d] opacity-25 blur-3xl" />
      </>

      <div className="relative z-10 mx-auto w-full max-w-6xl py-4 md:py-10">
        <div className="mb-4 flex justify-end">
          <LanguageSwitcher />
        </div>
      <Card className="mx-auto w-full max-w-2xl rounded-[1.6rem] border-4 border-[#151827] bg-white p-6 md:p-8" style={{ boxShadow: '14px 14px 0 #151827' }}> {/* Iets breder gemaakt voor de extra velden */}
        <div className="text-center mb-8">
          {useUnifiedStyle && (
            <div className="mb-4 inline-block bg-[#3f7f65] px-4 py-2 text-xs font-black uppercase tracking-wider text-white" style={{ clipPath: 'polygon(0 14%, 96% 0, 100% 84%, 8% 100%)' }}>
              {t('auth.brand_tagline')}
            </div>
          )}
          <h1 className="text-2xl font-black text-[#3f7f65]">
            {type === 'company' ? 'Business Portal' : 'Talent Portal'}
          </h1>
          <p className="mt-1 text-xs font-black uppercase tracking-[0.2em] text-[#151827]/65">{type === 'company' ? 'werepresenttalent.nl' : 'Betaaldsolliciteren.nl'}</p>
          <p className="mt-2 text-sm text-gray-500">
            {isRegistering ? t('auth.create_new_account') : t('auth.login_to_dashboard')}
          </p>
          {isRegistering && (
            <p className="mt-1 text-sm text-gray-500">{t('auth.post_register_access')}</p>
          )}
        </div>

        {error && (
            <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                {error}
            </div>
        )}

        {!isRegistering && isCandidate && unverifiedLoginTargetEmail && (
          <div className="mb-4 rounded-lg border-2 border-[#151827] bg-[#eef7f2] p-4">
            <p className="text-sm text-blue-800 mb-3">
              {t('auth.unverified_banner')}
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={handleResendFromLoginBanner}
                disabled={resendBusy || resendCooldown > 0}
                className="flex-1 rounded border-2 border-[#151827] py-2 font-medium text-[#151827] transition hover:bg-white disabled:opacity-50"
              >
                {resendBusy
                  ? t('auth.sending')
                  : resendCooldown > 0
                    ? t('auth.new_code_in_seconds', { seconds: resendCooldown })
                    : t('auth.send_new_code')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setPendingVerification({ email: unverifiedLoginTargetEmail });
                  setVerificationCode('');
                  setError(null);
                }}
                className="flex-1 rounded bg-[#3f7f65] py-2 font-medium text-white transition hover:translate-x-0.5 hover:translate-y-0.5"
                style={{ boxShadow: '6px 6px 0 #151827' }}
              >
                {t('auth.enter_code')}
              </button>
            </div>
          </div>
        )}

        {/* TABS */}
        <div className="mb-6 flex border-b border-[#151827]">
          <button 
            onClick={() => { setIsRegistering(false); setError(null); setUnverifiedLoginEmail(''); }}
            className={`flex-1 pb-2 text-sm font-black uppercase tracking-wider ${!isRegistering ? 'border-b-4 border-[#3f7f65] text-[#3f7f65]' : 'text-gray-500'}`}
          >
            {t('auth.login')}
          </button>
          <button 
            onClick={() => { setIsRegistering(true); setError(null); setUnverifiedLoginEmail(''); }}
            className={`flex-1 pb-2 text-sm font-black uppercase tracking-wider ${isRegistering ? 'border-b-4 border-[#3f7f65] text-[#3f7f65]' : 'text-gray-500'}`}
          >
            {t('auth.register')}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
            
            {/* HONEYPOT - Invisible field to catch bots */}
            <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                <label htmlFor="website">Website (leave empty)</label>
                <input 
                    type="text"
                    id="website"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={e => setFormData({...formData, website: e.target.value})}
                />
            </div>
            
            {/* STANDAARD CREDENTIALS - Email is nu de identifier */}
            {renderInput(t('auth.email'), "email", "email", "", true)}
            <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                {t('auth.password')}
                    <span className="text-red-500 ml-1">*</span>
                </label>
                <input 
                    required
                    type="password"
                placeholder={isRegistering ? t('auth.password_min_8') : ""}
                    minLength={isRegistering ? 8 : undefined}
                    className={useUnifiedStyle
                      ? 'w-full rounded p-3 min-h-[44px] text-sm transition-colors border-2 border-[#151827] bg-[#fffdf8] focus:bg-white focus:border-[#3f7f65] focus:outline-none'
                      : companyFieldClass}
                    value={formData.password}
                    onChange={e => setFormData({...formData, password: e.target.value})}
                />
                {isRegistering && formData.password && formData.password.length < 8 && (
                  <p className="text-xs text-orange-500 mt-1">{t('auth.characters_needed', { count: 8 - formData.password.length })}</p>
                )}
                {!isRegistering && (
                    <button 
                        type="button"
                        onClick={() => { setShowForgotPassword(true); setError(null); }}
                        className={`text-xs mt-1 hover:underline ${isCandidate ? 'text-[#3f7f65]' : 'text-brand-purple'}`}
                    >
                      {t('auth.forgot_password')}
                    </button>
                )}
            </div>

            {/* REGISTRATIE VELDEN */}
            {isRegistering && (
                <div className="animate-fade-in-up space-y-4 border-t-2 border-[#151827]/15 pt-4">
                  <h3 className="text-sm font-bold text-[#3f7f65]">
                        {t('auth.contact_details')}
                        <span className="text-xs text-gray-500 font-normal ml-2">(<span className="text-red-500">*</span> = {t('common.required').toLowerCase()})</span>
                    </h3>
                    {/* Gedeelde velden */}
                    {renderInput(type === 'company' ? t('auth.contact_name') : t('personalia.full_name'), "name", "text", "", true)}
                    {renderInput(t('personalia.phone'), "phone", "tel", "", type === 'company')}
                    {renderLanguagePreferenceSelect()}

                    {/* --- SPECIFIEK VOOR BEDRIJVEN --- */}
                    {type === 'company' && (
                        <>
                        <h3 className="mt-4 text-sm font-bold uppercase tracking-[0.12em] text-[#151827]">{t('auth.organization_details')}</h3>
                          {renderInput(t('personalia.company_name'), "company_name", "text", "", true)}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {renderInput(t('personalia.sector'), "sector", "text", "", true)}
                            {renderInput(t('personalia.location_company'), "location", "text", "", true)}
                            </div>
                          {renderInput(t('auth.contact_role'), "function", "text", "", true)}
                            
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                              {t('personalia.one_liner')}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <textarea 
                                    required
                                  className="w-full rounded p-3 min-h-[44px] text-sm transition-colors border-2 border-[#151827] bg-[#fffdf8] focus:bg-white focus:ring-0 focus:border-[#3f7f65] resize-none"
                                    rows={2}
                              placeholder={t('auth.company_one_liner_placeholder')}
                                    value={formData.one_liner_description}
                                    onChange={e => setFormData({...formData, one_liner_description: e.target.value})}
                                />
                            </div>
                        </>
                    )}

                    {/* --- SPECIFIEK VOOR KANDIDATEN --- */}
                    {type === 'candidate' && (
                        <>
                            <h3 className="mt-4 text-sm font-bold text-[#3f7f65]">{t('auth.profile_details')}</h3>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                {/* Geslacht - nieuw veld */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">{t('personalia.gender')}</label>
                                    <select
                                        className="w-full min-h-[44px] rounded border-2 border-[#151827] bg-[#fffdf8] p-3 text-sm transition-colors focus:border-[#3f7f65] focus:bg-white focus:outline-none"
                                        value={formData.gender}
                                        onChange={e => setFormData({...formData, gender: e.target.value})}
                                    >
                                        <option value="">{t('auth.select_placeholder')}</option>
                                        <option value="man">{t('personalia.gender_male')}</option>
                                        <option value="vrouw">{t('personalia.gender_female')}</option>
                                        <option value="anders">{t('personalia.gender_other')}</option>
                                    </select>
                                </div>
                                    {renderInput(t('personalia.age'), "age", "number", "", false)}
                                    {renderInput(t('personalia.location'), "location", "text", "", false)}
                            </div>
                            
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                                  {t('personalia.education_history')}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <textarea 
                                    required
                                    className="w-full rounded border-2 border-[#151827] bg-[#fffdf8] p-3 text-sm focus:border-[#3f7f65] focus:bg-white focus:outline-none"
                                    rows={3}
                                    placeholder={t('auth.education_placeholder')}
                                    value={formData.education_history}
                                    onChange={e => setFormData({...formData, education_history: e.target.value})}
                                />
                            </div>

                            {/* Werkervaring - nieuw veld */}
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">{t('personalia.work_experience')}</label>
                                <p className="text-xs text-gray-500 italic mb-2">
                                  {t('auth.work_experience_hint')}
                                </p>
                                <textarea 
                                    className="w-full rounded border-2 border-[#151827] bg-[#fffdf8] p-3 text-sm focus:border-[#3f7f65] focus:bg-white focus:outline-none"
                                    rows={3}
                                    placeholder={t('auth.work_experience_placeholder')}
                                    value={formData.work_experience}
                                    onChange={e => setFormData({...formData, work_experience: e.target.value})}
                                />
                            </div>
                        </>
                    )}

                    {/* AVG CONSENT CHECKBOXES */}
                    <div className="mt-4 space-y-3 border-t-2 border-[#151827]/15 pt-4">
                        <h3 className="text-sm font-bold text-gray-700">
                          {t('auth.terms_privacy')}
                          <span className="text-xs text-gray-500 font-normal ml-2">({t('auth.all_required').toLowerCase()})</span>
                        </h3>
                        
                        <label className="flex items-start gap-3 cursor-pointer group">
                            <input 
                                type="checkbox"
                                required
                                checked={formData.consent_terms}
                                onChange={e => setFormData({...formData, consent_terms: e.target.checked})}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#3f7f65] focus:ring-[#3f7f65]"
                            />
                            <span className="text-sm text-gray-600 group-hover:text-gray-800">
                              {t('auth.accept_terms_prefix')}{' '}
                              <a href={WEREPRESENT_LINKS.voorwaarden} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                {t('auth.general_terms')}
                                </a>
                                <span className="text-red-500 ml-1">*</span>
                            </span>
                        </label>
                        
                        <label className="flex items-start gap-3 cursor-pointer group">
                            <input 
                                type="checkbox"
                                required
                                checked={formData.consent_privacy}
                                onChange={e => setFormData({...formData, consent_privacy: e.target.checked})}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#3f7f65] focus:ring-[#3f7f65]"
                            />
                            <span className="text-sm text-gray-600 group-hover:text-gray-800">
                              {t('auth.accept_privacy_prefix')}{' '}
                              <a href={WEREPRESENT_LINKS.privacy} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                {t('auth.privacy_policy')}
                                </a>
                              {' '}{t('auth.read_and_accept')}
                                <span className="text-red-500 ml-1">*</span>
                            </span>
                        </label>
                        
                        <label className="flex items-start gap-3 cursor-pointer group">
                            <input 
                                type="checkbox"
                                required
                                checked={formData.consent_ai}
                                onChange={e => setFormData({...formData, consent_ai: e.target.checked})}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#3f7f65] focus:ring-[#3f7f65]"
                            />
                            <span className="text-sm text-gray-600 group-hover:text-gray-800">
                              {t('auth.accept_ai_prefix')}{' '}
                              <a href={WEREPRESENT_LINKS.aiPolicy} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                    AI Policy
                                </a>
                              {' '}{t('auth.ai_processing_consent')}
                                <span className="text-red-500 ml-1">*</span>
                            </span>
                        </label>
                    </div>
                </div>
            )}

            <button 
                type="submit" 
                disabled={busy}
              className="mt-6 w-full rounded-xl bg-[#3f7f65] py-3 font-black text-white transition hover:translate-x-0.5 hover:translate-y-0.5 disabled:opacity-50"
                style={{ boxShadow: '8px 8px 0 #151827' }}
            >
              {busy ? t('auth.processing') : (isRegistering ? t('auth.create_account_continue') : t('auth.login'))}
            </button>

        </form>
      </Card>
            </div>

      {/* Wachtwoord Vergeten Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="relative w-full max-w-md rounded-[1.4rem] border-4 border-[#151827] p-6 animate-fade-in-up" style={{ boxShadow: '10px 10px 0 #151827' }}>
            <button 
              onClick={resetForgotPasswordState}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl"
            >
              ✕
            </button>

            <h2 className="mb-4 text-xl font-black text-[#3f7f65]">
              {t('auth.reset_password')}
            </h2>

            {error && (
              <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                {error}
              </div>
            )}

            {forgotStep === 'success' ? (
              <div className="text-center py-4">
                <div className="text-4xl mb-4">✓</div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">{t('auth.password_changed_title')}</h3>
                <p className="text-gray-600 mb-4">{t('auth.reset_success')}</p>
                <button
                  onClick={resetForgotPasswordState}
                  className="rounded-xl bg-[#3f7f65] px-6 py-2 font-black text-white"
                >
                  {t('auth.back_to_login')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
                {forgotStep === 'email' && (
                  <>
                    <p className="text-sm text-gray-600 mb-4">
                      {t('auth.reset_email_intro')}
                    </p>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 uppercase mb-1">{t('auth.email')}</label>
                      <input 
                        required
                        type="email"
                        placeholder={t('auth.email_placeholder')}
                        className={`w-full min-h-[44px] p-3 text-sm ${isCandidate
                          ? 'rounded border-2 border-[#151827] bg-[#fffdf8] focus:border-[#3f7f65] focus:bg-white focus:outline-none'
                          : companyFieldClass}`}
                        value={forgotEmail}
                        onChange={e => setForgotEmail(e.target.value)}
                      />
                    </div>
                  </>
                )}

                {forgotStep === 'code' && (
                  <>
                    <div className="p-3 bg-green-50 text-green-700 text-sm rounded border border-green-200 mb-4">
                      {t('auth.code_sent_to', { email: forgotEmail })}
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 uppercase mb-1">{t('auth.verification_code')}</label>
                      <input 
                        required
                        type="text"
                        placeholder="000000"
                        maxLength={6}
                        pattern="[0-9]{6}"
                        className={isCandidate
                          ? 'w-full rounded border-2 border-[#151827] bg-[#fffdf8] p-2 text-center text-xl tracking-widest font-mono focus:border-[#3f7f65] focus:bg-white focus:outline-none'
                          : `${companyFieldClass} text-center text-xl tracking-widest font-mono`}
                        value={forgotCode}
                        onChange={e => setForgotCode(e.target.value.replace(/\D/g, ''))}
                      />
                      <p className="text-xs text-gray-500 mt-1">{t('auth.check_spam')}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 uppercase mb-1">{t('auth.reset_new_password')}</label>
                      <input 
                        required
                        type="password"
                        placeholder={t('auth.password_min_8')}
                        minLength={8}
                        className={`w-full min-h-[44px] p-3 text-sm ${isCandidate
                          ? 'rounded border-2 border-[#151827] bg-[#fffdf8] focus:border-[#3f7f65] focus:bg-white focus:outline-none'
                          : companyFieldClass}`}
                        value={forgotNewPassword}
                        onChange={e => setForgotNewPassword(e.target.value)}
                      />
                      {forgotNewPassword && forgotNewPassword.length < 8 && (
                        <p className="text-xs text-orange-500 mt-1">{t('auth.characters_needed', { count: 8 - forgotNewPassword.length })}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 uppercase mb-1">{t('auth.password_confirm')}</label>
                      <input 
                        required
                        type="password"
                        placeholder={t('auth.repeat_password')}
                        className={`w-full min-h-[44px] p-3 text-sm ${isCandidate
                          ? 'rounded border-2 border-[#151827] bg-[#fffdf8] focus:border-[#3f7f65] focus:bg-white focus:outline-none'
                          : companyFieldClass}`}
                        value={forgotConfirmPassword}
                        onChange={e => setForgotConfirmPassword(e.target.value)}
                      />
                      {forgotConfirmPassword && forgotNewPassword !== forgotConfirmPassword && (
                        <p className="text-xs text-red-500 mt-1">{t('auth.password_mismatch')}</p>
                      )}
                    </div>
                  </>
                )}

                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={resetForgotPasswordState}
                    className={`flex-1 py-2 text-gray-600 hover:bg-gray-50 ${isCandidate ? 'rounded border-2 border-[#151827]' : 'border-4 border-[#151827] bg-white font-black uppercase tracking-[0.12em] text-[#151827]'}`}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={busy}
                    className={`flex-1 py-2 text-white disabled:opacity-50 ${isCandidate ? 'rounded-xl bg-[#3f7f65] font-black' : 'border-4 border-[#151827] bg-[#7a4793] font-black uppercase tracking-[0.12em] hover:bg-[#151827]'}`}
                    style={isCandidate ? { boxShadow: '6px 6px 0 #151827' } : undefined}
                  >
                    {busy ? t('auth.processing') : forgotStep === 'email' ? t('auth.send_code') : t('auth.reset_password')}
                  </button>
                </div>
              </form>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}